import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { getVisibleTextColor, isTextColorVisible } from '../../helpers/colors';
import { palette } from '../../style/variables';
import tinycolor from '@ctrl/tinycolor';

@customElement('form-button')
export class FormButton extends LitElement {
    @property({ type: String })
    type?: string;

    @property({ type: Boolean })
    disabled?: boolean;

    @property({ type: Boolean })
    fullWidth: boolean = false;

    @property({ type: Boolean })
    fullHeight: boolean = false;

    @property({ type: String })
    borderStyle?: string;

    @property({ type: String })
    size?: string = 'md';

    @property({ type: Boolean })
    customFont?: boolean = false;

    @property({ type: String })
    variant?: string = 'raised';

    @property({ type: Boolean })
    vertical?: boolean = false;

    @property({ type: String })
    backgroundColor?: string;

    @property({ type: String })
    textColor?: string;

    @property({ type: String })
    icon?: string;

    @state()
    private hasText: boolean = true;

    connectedCallback() {
        super.connectedCallback();
        if (this.borderStyle === 'round') {
            this.style.setProperty('--btn-border-radius', '1.5rem');
        } else if (this.borderStyle === 'side') {
            this.style.setProperty('--btn-border-radius', '0 0.5rem 0.5rem 0');
        } else if (this.borderStyle === 'none') {
            this.style.setProperty('--btn-border-radius', '0');
        } else {
            this.style.setProperty('--btn-border-radius', '4px');
        }

        if (this.size === 'sm') {
            this.style.setProperty('--btn-font-size', '12px');
            this.style.setProperty('--btn-padding-y', '0.3rem');
            this.style.setProperty('--mdc-icon-size', '16px');
        } else if (this.size === 'md') {
            this.style.setProperty('--btn-font-size', '0.8rem');
            this.style.setProperty('--btn-padding-y', '0.4rem');
            this.style.setProperty('--btn-padding-x', '1rem');
            this.style.setProperty('--mdc-icon-size', '24px');
        } else if (this.size === 'lg') {
            this.style.setProperty('--btn-font-size', '1rem');
            this.style.setProperty('--btn-padding-y', '0.6rem');
            this.style.setProperty('--btn-padding-x', '1rem');
            this.style.setProperty('--mdc-icon-size', '32px');
        } else if (this.size === 'xs') {
            this.style.setProperty('--btn-font-size', '0.2rem');
            this.style.setProperty('--btn-padding-y', '0.25rem');
            this.style.setProperty('--btn-padding-x', '0.5rem');
            this.style.setProperty('--mdc-icon-size', '12px');
        }

        if (this.customFont === true) {
            this.style.setProperty('font-family', 'var(--custom-font))');
        }

        if (this.variant === 'outline') {
            this.style.setProperty('--btn-background-color', 'transparent');
            this.style.setProperty('--btn-hover-opacity', '1');
            this.style.setProperty('--btn-hover-color', 'var(--main-primary-opacity-color)');
            this.style.setProperty('--btn-border-color', `${this.textColor ?? 'var(--main-primary-color)'}`);
            this.style.setProperty('--btn-label-color', 'var(--main-primary-color)');
        } else if (this.variant === 'outlinewarning') {
            this.style.setProperty('--btn-background-color', 'transparent');
            this.style.setProperty('--btn-hover-opacity', '1');
            this.style.setProperty('--btn-hover-color', 'var(--main-primary-opacity-color)');
            this.style.setProperty('--btn-border-color', 'var(--main-invalid-color)');
            this.style.setProperty('--btn-label-color', 'var(--main-invalid-color)');
        } else if (this.variant === 'social') {
            this.style.setProperty('--btn-background-color', 'transparent');
            this.style.setProperty('--btn-hover-opacity', '1');
            this.style.setProperty('--btn-hover-color', 'var(--main-primary-color)');
            this.style.setProperty('--btn-border-color', 'var(--main-primary-color)');
            this.style.setProperty('--btn-label-color', 'var(--main-primary-color)');
        } else if (this.variant === 'navbutton') {
            this.style.setProperty('--btn-background-color', 'transparent');
            this.style.setProperty('--btn-hover-opacity', '1');
            this.style.setProperty('--btn-hover-color', 'var(--main-primary-color)');
            this.style.setProperty('--btn-border-color', 'transparent');
            this.style.setProperty('--btn-label-color', 'var(--main-primary-color)');
            this.style.setProperty('--btn-padding-x', '5px');
        } else if (this.variant === 'transparentoutline') {
            this.style.setProperty('--btn-hover-opacity', '1');
            this.style.setProperty('--btn-background-color', '#FFFFFF50');
            this.style.setProperty('--btn-hover-color', 'var(--main-primary-color)');
        // eslint-disable-next-line no-empty
        } else if (this.variant === 'custom') {
        } else if (this.variant === 'horizontaltall') {
            this.style.setProperty('--btn-flex-direction', 'column');
            this.style.setProperty('--btn-white-space', 'wrap');
            this.style.setProperty('justify-content', 'space-evenly');
        } else if (this.variant === 'heavy') {
            this.style.setProperty('--btn-hover-color', 'var(--btn-background-color)');
            this.style.setProperty('--btn-border-color', 'transparent');
            this.style.setProperty('--btn-label-color', 'var(--theme-white-color)');
            this.style.setProperty('--btn-font-weight', '600');
            this.style.setProperty('--btn-shadow', '0 4px 12px rgba(0, 0, 0, 0.25)');
            this.style.setProperty('--btn-hover-shadow', '0 8px 24px rgba(0, 0, 0, 0.35)');
        } else if (this.variant === 'heavyoutline') {
            this.style.setProperty('--btn-hover-color', 'var(--btn-background-color)');
            this.style.setProperty('--btn-border-color', `${this.textColor ?? 'var(--main-primary-color)'}`);
            this.style.setProperty('--btn-label-color', `${this.textColor ?? 'var(--main-primary-color)'}`);
            this.style.setProperty('--btn-font-weight', '600');
            this.style.setProperty('--btn-background-color', 'transparent');
            this.style.setProperty('--btn-shadow', '0 4px 12px rgba(0, 0, 0, 0.25)');
            this.style.setProperty('--btn-hover-shadow', '0 8px 24px rgba(0, 0, 0, 0.35)');
        } else if (this.variant === 'circlefab') {
            this.style.setProperty('--btn-border-radius', '50%');
            this.style.setProperty('--btn-padding-y', '0.5rem');
            this.style.setProperty('--btn-padding-x', '0.5rem');
            this.style.setProperty('--mdc-icon-size', '36px');
        }
        else {
            this.style.setProperty('--btn-hover-color', 'var(--btn-background-color)');
            this.style.setProperty('--btn-border-color', 'transparent');
            this.style.setProperty('--btn-label-color', 'var(--theme-white-color)');

            // if background color is white, set  border color to theme border color
            if (this.backgroundColor?.toLowerCase() === '#ffffff' || this.backgroundColor === 'white' || this.backgroundColor === `var('--theme-white-color'))` || this.backgroundColor?.toLowerCase() === '#fff') {
                this.style.setProperty('--btn-border-color', this.textColor ?? 'var(--theme-border-color)');
            }
        }
        if (this.vertical === false) {
            this.style.setProperty('--btn-flex-direction', 'row');
            this.style.setProperty('--btn-white-space', 'nowrap');
        } else {
            this.style.setProperty('--btn-flex-direction', 'column');
            this.style.setProperty('--btn-white-space', 'wrap');
            this.style.setProperty('--btn-padding-y', '0.25rem');
        }

        if (this.backgroundColor) {
            this.updateBackgroundColorStyles(this.backgroundColor);
        }

        if (this.textColor) {
            this.style.setProperty('--btn-label-color', this.textColor);
        }

        // Outline-style buttons paint their label/border straight onto the white
        // card, so a near-white color (e.g. a brand color that's white) vanishes.
        // Fall back to a readable color when the resolved one won't show on white.
        if (this.variant === 'outline' || this.variant === 'heavyoutline' || this.variant === 'social') {
            const outlineColor = this.textColor ?? palette.primaryBlue;
            if (!isTextColorVisible('#ffffff', outlineColor)) {
                this.style.setProperty('--btn-label-color', palette.primaryBlue);
                this.style.setProperty('--btn-border-color', palette.primaryBlue);
            }
        }

        this.hasText = !!this.textContent?.trim();
    }

    // listen for background color changes
    updated(changedProperties: Map<string, any>) {
        if (changedProperties.has('backgroundColor')) {
            if (this.backgroundColor) {
                this.updateBackgroundColorStyles(this.backgroundColor);
            }
        }
        if (changedProperties.has('textColor')) {
            if (this.textColor) {
                this.style.setProperty('--btn-label-color', this.textColor);
            }
        }
    }

    updateBackgroundColorStyles(backgroundColor: string) {
        this.style.setProperty('--btn-background-color', backgroundColor);
        const textColor = getVisibleTextColor(backgroundColor);
        this.style.setProperty('--btn-label-color', textColor);
        // set hover color to 20% darker
        const hoverColor = tinycolor(backgroundColor).darken(20).toString();
        this.style.setProperty('--btn-hover-color', hoverColor);
    }

    protected render() {
        return html`
            <button
                class="form-btn"
                type="${this.type}"
                ?disabled=${this.disabled}
                style="width: ${this.fullWidth ? '100%' : 'auto'} !important; height: ${this.fullHeight ? '100%' : 'auto'} !important; cursor: ${this.disabled === true ? 'not-allowed' : 'pointer'}"
            >
                ${this.icon ? html`<mwc-icon style="font-size: var(--mdc-icon-size); color: ${this.textColor ?? 'var(--btn-label-color)'};${this.hasText ? 'margin-right: 5px;' : ''}">${this.icon}</mwc-icon>` : ''}
                <slot></slot>
            </button>
        `;
    }

    static get styles() {
        return css`
            .form-btn {
                display: flex;
                flex-direction: var(--btn-flex-direction);
                justify-content: center;
                align-items: center;
                width: 100%;
                color: var(--btn-label-color);
                text-align: center;
                text-decoration: none;
                white-space: var(--btn-white-space);
                vertical-align: middle;
                cursor: pointer;
                -webkit-user-select: none;
                -moz-user-select: none;
                user-select: none;
                background-color: var(--btn-background-color, ${unsafeCSS(palette.primaryBlue)});
                border: 1px solid var(--btn-border-color);
                padding: var(--btn-padding-y) var(--btn-padding-x, 0.5rem);
                font-size: var(--btn-font-size) 0.8rem;
                font-weight: var(--btn-font-weight, 400);
                border-radius: var(--btn-border-radius);
                transition: all 0.2s ease-in-out;
                font-family: var(--main-font);
                box-shadow: var(--btn-shadow, none);
            }

            .form-btn:hover {
                opacity: var(--btn-hover-opacity, 0.8);
                color: var(--theme-white-color);
                background-color: var(--btn-hover-color, ${unsafeCSS(palette.primaryBlue)});
                box-shadow: var(--btn-hover-shadow, var(--btn-shadow, none));
                transform: var(--btn-hover-transform, none);
            }

            .form-btn:disabled {
                color: var(--main-moon-color);
                cursor: not-allowed;
            }

            @media screen and (max-width: 900px) {
                .form-btn {
                    font-size: 0.8rem;
                    line-height: 1;
                }
            }
        `;
    }
}
